import { useEffect, useMemo, useState } from "react";
import { Button, Modal, ModalBody } from "flowbite-react";
import { Lottie } from "lottie-react";
import { AlertTriangle, Clock3 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import expiredAnimation from "@/assets/images/shop/expired.json";
import { CART_QUERY_KEY } from "@/features/cart/constants/cart.constants";
import { useCartReservationTimer } from "@/features/cart/hooks/useCartReservationTimer";
import { cartService } from "@/features/cart/services/cart.service";
import type { CartResponse } from "@/features/cart/types/cart.type";

const handledExpirationKeys = new Set<string>();

type CartReservationTimerProps = {
  cart?: CartResponse
  variant?: "banner" | "inline"
  className?: string
};

export const CartReservationTimer = ({
  cart,
  variant = "banner",
  className = "",
}: CartReservationTimerProps) => {
  const {
    expiresAt,
    formatted,
    isExpired,
    isExpiringSoon,
  } = useCartReservationTimer(cart);
  const [showExpirationDialog, setShowExpirationDialog] = useState(false);
  const queryClient = useQueryClient();
  const expirationKey = expiresAt && cart?.id
    ? `${cart.id}-${expiresAt.getTime()}`
    : null;

  const clearCartMutation = useMutation({
    mutationFn: () => cartService.clearCart(),
    onSuccess: (data) => {
      queryClient.setQueryData(CART_QUERY_KEY, data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
    },
  });

  useEffect(() => {
    if (!expiresAt || !isExpired || !expirationKey) {
      return;
    }

    if (handledExpirationKeys.has(expirationKey)) {
      return;
    }

    handledExpirationKeys.add(expirationKey);
    clearCartMutation.mutate();
    setShowExpirationDialog(true);
  }, [clearCartMutation, expirationKey, expiresAt, isExpired]);

  const hasLimitedTickets = useMemo(
    () => Boolean(cart?.items.some((item) =>
      item.item_type === "rate" || item.category === "DIRECT_STOCK",
    )),
    [cart],
  );

  const shouldRenderTimer = Boolean(cart && hasLimitedTickets && expiresAt);

  if (!shouldRenderTimer && !showExpirationDialog) {
    return null;
  }

  return (
    <>
      {shouldRenderTimer && (variant === "inline"
        ? (
          <div
            className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${isExpired
              ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-100"
              : isExpiringSoon
                ? "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-100"
                : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100"
            } ${className}`}
          >
            <Clock3 className="h-3.5 w-3.5" />
            <span>
              {isExpired ? "Réservation expirée" : `Temps restant : ${formatted}`}
            </span>
          </div>
        )
        : (
          <div
            className={`rounded-lg border p-4 ${isExpired
              ? "border-red-200 bg-red-50 text-red-800 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-100"
              : isExpiringSoon
                ? "border-orange-200 bg-orange-50 text-orange-900 dark:border-orange-800/60 dark:bg-orange-900/40 dark:text-orange-100"
                : "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-900/40 dark:text-emerald-100"
            } ${className}`}
          >
            <div className="flex items-start gap-3">
              {isExpired
                ? <AlertTriangle className="mt-0.5 shrink-0 h-5 w-5" />
                : <Clock3 className="mt-0.5 shrink-0 h-5 w-5" />}
              <div className="text-sm leading-relaxed">
                <p className="font-semibold">
                  Billets réservés temporairement
                </p>
                <p className="mt-1">
                  {isExpired
                    ? "La durée de réservation est dépassée. Veuillez mettre à jour votre panier pour réserver à nouveau vos billets."
                    : (
                      <>
                        Temps restant pour finaliser votre commande:
                        {" "}
                        <span className="font-mono font-bold">
                          {formatted}
                        </span>
                        . Au-delà, les places seront remises en vente.
                      </>
                    )}
                </p>
              </div>
            </div>
          </div>
        ))}

      <Modal
        show={showExpirationDialog}
        size="md"
        onClose={() => setShowExpirationDialog(false)}
      >
        <ModalBody>
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <div className="h-32 w-32">
              <Lottie
                src={expiredAnimation}
                autoplay
                loop
                className="h-full w-full"
              />
            </div>
            <div className="space-y-1">
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                Réservation expirée
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Votre temps de réservation est écoulé. Les billets ont été libérés et votre panier a été réinitialisé.
              </p>
            </div>
            <Button
              color="primary"
              className="w-full sm:w-auto"
              onClick={() => {
                setShowExpirationDialog(false);
                queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
              }}
            >
              Actualiser mon panier
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

CartReservationTimer.displayName = "CartReservationTimer";
